export type ThemeMode = 'auto' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'lilo.theme'

const THEME_MODES = new Set<ThemeMode>(['auto', 'light', 'dark'])

export function normalizeThemeMode(value: unknown): ThemeMode {
  return typeof value === 'string' && THEME_MODES.has(value as ThemeMode)
    ? value as ThemeMode
    : 'auto'
}

export function readThemeMode(storage: Storage | undefined = getBrowserStorage()): ThemeMode {
  if (!storage) return 'auto'
  try {
    return normalizeThemeMode(storage.getItem(THEME_STORAGE_KEY))
  } catch {
    return 'auto'
  }
}

export function resolveThemeMode(mode: ThemeMode, media: Pick<Window, 'matchMedia'> | undefined = getBrowserWindow()): ResolvedTheme {
  if (mode === 'light' || mode === 'dark') return mode
  try {
    return media?.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

export function applyThemeMode(mode: ThemeMode, root: HTMLElement | undefined = getDocumentRoot()): void {
  if (!root) return
  if (mode === 'auto') {
    root.removeAttribute('data-theme')
    return
  }
  root.setAttribute('data-theme', mode)
}

export function setThemeMode(
  mode: ThemeMode,
  opts: {
    storage?: Storage
    root?: HTMLElement
  } = {}
): ThemeMode {
  const normalized = normalizeThemeMode(mode)
  const storage = opts.storage ?? getBrowserStorage()
  if (storage) {
    try {
      if (normalized === 'auto') {
        storage.removeItem(THEME_STORAGE_KEY)
      } else {
        storage.setItem(THEME_STORAGE_KEY, normalized)
      }
    } catch {
      // Theme still applies for this runtime even if browser storage is blocked.
    }
  }
  applyThemeMode(normalized, opts.root)
  return normalized
}

export function applyStoredThemeMode(): ThemeMode {
  const mode = readThemeMode()
  applyThemeMode(mode)
  return mode
}

function getBrowserStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined
  return window.localStorage
}

function getBrowserWindow(): Pick<Window, 'matchMedia'> | undefined {
  return typeof window === 'undefined' ? undefined : window
}

function getDocumentRoot(): HTMLElement | undefined {
  return typeof document === 'undefined' ? undefined : document.documentElement
}
